import { useCallback, useEffect, useMemo, useState } from 'react';

const trimToString = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
};

const normalizePayload = (values = {}) => ({
  proceso_id: Number(values.proceso_id) || null,
  nombre: trimToString(values.nombre),
  categoria_id: values.categoria_id || '',
  tiempo_vida_minutos: Number(values.tiempo_vida_minutos) || 0,
  tiempo_preparacion_min: Number(values.tiempo_preparacion_min) || 0,
  porcion: Number(values.porcion) || 0,
  unidad: trimToString(values.unidad),
  unidades: trimToString(values.unidades),
  articulo_id_boh: trimToString(values.articulo_id_boh),
  activo: Boolean(values.activo),
  imagen_url: values.imagen_url || null
});

const mapProcesses = (payload = []) =>
  payload.filter(Boolean).map((process) => ({
    id: process.id,
    nombre: process.nombre || 'Sin nombre'
  }));

const mapCategories = (payload = []) =>
  payload.filter(Boolean).map((category) => ({
    id: category.id,
    nombre: category.nombre || 'Sin nombre',
    activo: Boolean(category.activo)
  }));

export const useAdminArticles = () => {
  const [items, setItems] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [processFilter, setProcessFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const updateLocalHoursFlag = useCallback((articleId, enabled) => {
    if (!articleId) {
      return;
    }
    setItems((prev) => prev.map((item) => (
      item.id === articleId ? { ...item, tiene_horarios_configurados: enabled } : item
    )));
  }, []);

  const fetchArticles = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const query = processFilter ? `?process_id=${processFilter}` : '';
      const response = await fetch(`/admin/api/articles${query}`, { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los artículos');
      }
      if (!signal?.aborted) {
        setItems(data.articles || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar artículos');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [processFilter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchArticles(controller.signal);
    return () => controller.abort();
  }, [fetchArticles, refreshToken]);

  const fetchProcesses = useCallback(async (signal) => {
    try {
      const response = await fetch('/admin/api/processes/articulos', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los procesos');
      }
      if (!signal?.aborted) {
        setProcesses(mapProcesses(data.processes || []));
      }
    } catch (err) {
      if (!signal?.aborted) {
        console.warn('No se pudieron cargar los procesos:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchProcesses(controller.signal);
    return () => controller.abort();
  }, [fetchProcesses]);

  const fetchCategories = useCallback(async (signal) => {
    try {
      const response = await fetch('/admin/api/categories', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las categorías');
      }
      if (!signal?.aborted) {
        setCategories(mapCategories(data.categories || []));
      }
    } catch (err) {
      if (!signal?.aborted) {
        console.warn('No se pudieron cargar las categorías:', err.message);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchCategories(controller.signal);
    return () => controller.abort();
  }, [fetchCategories]);

  const fetchOperatingHours = useCallback(async (articleId) => {
    if (!articleId) {
      throw new Error('ID de artículo no válido.');
    }
    const response = await fetch(`/admin/api/articles/${articleId}/operating-hours`);
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudieron obtener los horarios');
    }
    return data?.article || {};
  }, []);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveArticle = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.proceso_id || !payload.nombre || !payload.unidad || !payload.unidades) {
      throw new Error('Completa todos los campos obligatorios.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/articles/${values.id}` : '/admin/api/articles';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el artículo');
    }
    return data;
  }, []);

  const deleteArticle = useCallback(async (id) => {
    const response = await fetch(`/admin/api/articles/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar el artículo');
    }
    return data;
  }, []);

  const uploadImage = useCallback(async ({ file, entityId }) => {
    if (!file) {
      throw new Error('Selecciona un archivo válido.');
    }
    const formData = new FormData();
    formData.append('image', file);
    formData.append('entity_type', 'article');
    if (entityId) {
      formData.append('entity_id', entityId);
    }
    const response = await fetch('/admin/api/upload-image', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo subir la imagen');
    }
    if (entityId) {
      const id = Number(entityId);
      setItems((prev) => prev.map((item) => (
        item.id === id ? { ...item, imagen_url: data.image_url } : item
      )));
    }
    return data;
  }, []);

  const saveOperatingHours = useCallback(async (articleId, config = null) => {
    if (!articleId) {
      throw new Error('ID de artículo no válido.');
    }
    const enabled = Boolean(config?.enabled);
    const rawHours = Array.isArray(config?.items) ? config.items : [];
    const operatingHours = enabled
      ? rawHours.map((item) => ({
        day_of_week: Number(item.day_of_week || item.day || 0),
        start_time: item.start_time || '',
        end_time: item.end_time || '',
        active: Boolean(item.active)
      })).filter((item) => item.day_of_week >= 1 && item.day_of_week <= 7)
      : [];

    const response = await fetch(`/admin/api/articles/${articleId}/operating-hours`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operating_hours: operatingHours })
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudieron guardar los horarios');
    }
    updateLocalHoursFlag(articleId, enabled && operatingHours.some((h) => h.active));
    return data;
  }, [updateLocalHoursFlag]);

  return useMemo(() => ({
    items,
    processes,
    categories,
    processFilter,
    setProcessFilter,
    loading,
    error,
    refresh,
    fetchOperatingHours,
    saveArticle,
    deleteArticle,
    uploadImage,
    saveOperatingHours
  }), [
    items,
    processes,
    categories,
    processFilter,
    loading,
    error,
    fetchOperatingHours,
    saveArticle,
    deleteArticle,
    uploadImage,
    saveOperatingHours
  ]);
};
